import { OrderUsageTicket } from '@shared/order/get/types';
import { OrderTicket, OrderTicketList, SelectNumber, TicketInfo } from '../../types';
import {
    DEV_ENV_FLAG,
    DISTRIBUTION_TYPE,
    NO_ADDRESS_EMAIL,
    ORDER_STATUS,
    REMAINING_ORDER_DISTRIBUTION_STATUS,
    RESERVATION_STATUS,
    TICKET_STATUS,
    TICKET_TYPE,
    USAGE_TICKET_STATUS,
} from '../Constants';
import { isInStartEnd } from '../dateUtils';
import { dayjsExt } from '../dateUtils/dayjsExtended';

/**
 * ローカル環境を判断
 *
 * @returns {Boolean}
 */
export const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/),
);

export const isEmptyObject = (obj?: object | null) => {
    if (typeof obj === 'object') {
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                return false; // オブジェクトが少なくとも1つのプロパティを持っている場合は空ではないと判定
            }
        }
    }
    return true; // それ以外の場合は空と判定
};

/**
 * 使用可能曜日が制限されているかどうか
 * @param {Array<string>} enableActivateWeekdays -使用開始可能曜日の配列
 * @returns {boolean}
 */
export const hasLimitedWeekdays = (enableActivateWeekdays: string[]): boolean => {
    return enableActivateWeekdays?.length < 8;
};

export const hasSelectedNumber = (selectedNumber: SelectNumber | null = {}) => {
    if (selectedNumber === null) {
        return false;
    }
    return Object.values(selectedNumber).some((value) => Number(value) > 0);
};

/**
 * 使用期限を表示するかチェック
 * @param {object} orderTicket - チケットの情報を含むオブジェクト
 * @returns {boolean} - (子券 or 単券のみ)チケットが「使用前」の状態であり、かつ少なくとも１つのチケットが「使用前」である場合、trueを返す
 */
export const shouldDisplayActivateDeadlineAt = (orderTicket: OrderTicket): boolean => {
    let order = orderTicket;
    if (isEmptyObject(order)) {
        return false;
    }
    const { comboItems } = order;

    // 子券 or 単券であること
    if (comboItems?.length > 1) {
        return false;
    }

    // 単券の省略が行われていない場合
    if (!order?.orderUsageTickets) {
        order = { ...order, ...comboItems[0] };
    }

    const { orderStatus, status, orderUsageTickets } = order;

    return (
        orderStatus === ORDER_STATUS.BOUGHT &&
        status === TICKET_STATUS.BOUGHT &&
        orderUsageTickets!.some((ticket: any) => ticket.status === USAGE_TICKET_STATUS.BOUGHT)
    );
};

/**
 * 使用開始を表示するかチェック
 * @param {object} orderTicket - チケットオブジェクト
 * @returns {boolean} - (子券 or 単券のみ)アクティブ化されている場合は true、それ以外の場合は false
 */
export const shouldDisplayActivatedAt = (orderTicket: OrderTicket): boolean => {
    let order = orderTicket;
    if (isEmptyObject(order)) {
        return false;
    }
    const { comboItems } = order;

    // 子券 or 単券であること
    if (comboItems?.length > 1) {
        return false;
    }

    // 単券の省略が行われていない場合
    if (!order?.orderUsageTickets) {
        order = { ...order, ...comboItems[0] };
    }

    const { orderUsageTickets, ticketType } = order;

    return !!(
        ticketType === TICKET_TYPE.TICKET &&
        orderUsageTickets?.some(
            (ticket: OrderUsageTicket) =>
                ticket.status === USAGE_TICKET_STATUS.ACTIVATED ||
                ticket.status === USAGE_TICKET_STATUS.EXPIRED ||
                ticket.status === USAGE_TICKET_STATUS.USED,
        )
    );
};

/**
 * 払戻期限を表示するかチェック
 * @param {object} orderTicket - チケットの情報を含むオブジェクト
 * @returns {boolean}
 */
export const shouldDisplayRefundDeadlineAt = (orderTicket: OrderTicket): boolean => {
    if (isEmptyObject(orderTicket)) {
        return false;
    }

    // 予約キャンセル済みの場合は払戻期限非表示
    if (isReservationCanceled(orderTicket) === true) {
        return false;
    }

    const { orderStatus, amount, refundFee } = orderTicket;

    // 子券チェック
    if (isChildTicket(orderTicket) || (refundFee ?? 0) >= (amount ?? 0)) {
        return false;
    }

    if (orderStatus === ORDER_STATUS.USER_REFUND_ENABLED || orderStatus === ORDER_STATUS.STOCK_USER_REFUND_ENABLED) {
        return true;
    }

    const isAllBoughtTicket =
        orderStatus === ORDER_STATUS.BOUGHT &&
        orderTicket.comboItems.every((item) =>
            item.orderUsageTickets?.every((ticket) => ticket.status === USAGE_TICKET_STATUS.BOUGHT),
        );
    return isAllBoughtTicket;
};

// デバイスIDのチェック
export const isSameDeviceId = (orderTicket: OrderTicket, currentDeviceId?: string | null): boolean => {
    if (isEmptyObject(orderTicket)) {
        return false;
    }
    const ticketActivatedDeviceId = orderTicket?.orderUsageTickets?.[0]?.activateDeviceId;
    if (!ticketActivatedDeviceId) {
        return true;
    }
    if (!currentDeviceId) {
        return true;
    }
    return ticketActivatedDeviceId === currentDeviceId;
};

/**
 * セット券の親券であること
 * @param {Object} ticket -ticketInfoまたはorderTicket
 * @returns {boolean}
 */
export const isComboTicket = (ticket: OrderTicket | TicketInfo): boolean => {
    if (isEmptyObject(ticket)) {
        return false;
    }
    return ticket.isCombination && ticket.comboItems.length > 1;
};

/**
 * セット券の子券であること
 * @param {Object} ticket -ticketInfoまたはorderTicket
 * @returns {boolean}
 */
export const isChildTicket = (ticket: OrderTicket): boolean => {
    if (isEmptyObject(ticket)) {
        return false;
    }
    return ticket.isCombination && ticket.comboItems.length === 1;
};

/**
 * 払戻済みであること
 * @param {Object} orderTicket
 * @returns {boolean}
 */
export const isRefunded = (orderTicket: OrderTicket | OrderTicketList): boolean => {
    if (isEmptyObject(orderTicket)) {
        return false;
    }
    const { orderStatus } = orderTicket;
    if (!orderStatus) {
        return false;
    }
    return [
        ORDER_STATUS.NORMAL_REFUND,
        ORDER_STATUS.WEB_REFUND,
        ORDER_STATUS.STOCK_USER_REFUNDED,
        ORDER_STATUS.USER_REFUNDED,
    ].includes(orderStatus);
};

/**
 * 入力値が数値であること。nullやundefinedの場合はfalseになる。
 * @param {any} value
 * @returns
 */
export const isNumber = (value: any): boolean => {
    return typeof value === 'number' && !isNaN(value);
};

/**
 * 予約チケットかチェック
 * @param {Object} orderTicket
 * @returns
 */
export const isReserveTicket = (orderTicket?: OrderTicket | TicketInfo | OrderTicketList): boolean => {
    if (isEmptyObject(orderTicket) || !orderTicket) return false;
    return orderTicket.comboItems?.some((item) => item.isReserve);
};

export const hasEmail = (user: any): boolean => {
    const email = user?.email;
    if (DEV_ENV_FLAG && email.includes(NO_ADDRESS_EMAIL)) {
        return false;
    }
    return !!email;
};

/**
 * 払戻中であること
 * @param {Object} orderTicket
 * @returns {boolean}
 */
export const isRefundPending = (orderTicket: OrderTicket | OrderTicketList): boolean => {
    if (isEmptyObject(orderTicket)) {
        return false;
    }
    const { orderStatus } = orderTicket;
    if (!orderStatus) {
        return false;
    }
    return [
        ORDER_STATUS.NORMAL_PENDING,
        ORDER_STATUS.WEB_PENDING,
        ORDER_STATUS.STOCK_USER_REFUND_PENDING,
        ORDER_STATUS.USER_REFUND_PENDING,
    ].includes(orderStatus);
};

/**
 * 分配済みチェック
 */
export const isDistributed = (orderTicket: OrderTicket | OrderTicketList) => {
    const { remainingNumberAdults, remainingNumberCustom1, remainingNumberCustom2, remainingNumberCustom3 } =
        orderTicket?.remainingOrderDistribution ?? {};

    const remainNumber =
        (remainingNumberAdults ?? 0) +
        (remainingNumberCustom1 ?? 0) +
        (remainingNumberCustom2 ?? 0) +
        (remainingNumberCustom3 ?? 0);

    return remainNumber === 0;
};

export const isQrCodeScanned = (
    remainingOrderDistributionStatus?: string | null,
    orderDistributionStatus?: string | null,
) => {
    return (
        remainingOrderDistributionStatus === REMAINING_ORDER_DISTRIBUTION_STATUS.USED ||
        orderDistributionStatus === REMAINING_ORDER_DISTRIBUTION_STATUS.USED
    );
};

/**
 * 分配可能チェック
 */
export const canDistribution = (orderTicket?: OrderTicket) => {
    if (!orderTicket) {
        return false;
    }
    const { remainingOrderDistribution } = orderTicket;

    if (isDistributed(orderTicket)) {
        return false;
    }

    //予約キャンセルされたこと
    if (isReservationCanceled(orderTicket)) return false;

    const remainingOrderDistributionStatus = remainingOrderDistribution?.remainingOrderDistributionStatus;

    // QR使用済みの場合は分配不可
    // 分配前レコードのステータスのみ確認する(分配後のチケットが利用されても、分配前チケットが使われない限り新たな分配はできる)
    if (isQrCodeScanned(remainingOrderDistributionStatus, undefined)) {
        return false;
    }

    // 未使用のチケットであること
    const isUnused = orderTicket.comboItems?.every((item) =>
        item.orderUsageTickets?.every((ticket) => ticket.status === USAGE_TICKET_STATUS.BOUGHT),
    );
    if (!isUnused) {
        return false;
    }
    const { activateDeadlineAt, availableStartAt, availableEndAt, orderStatus } = orderTicket;

    //チケットの購入状態が「購入済み」であること
    if (orderStatus !== ORDER_STATUS.BOUGHT) {
        return false;
    }

    // 利用開始可能期限のチェック
    const isBeforeActivateDeadline = dayjsExt().isBefore(dayjsExt(activateDeadlineAt));
    // 有効化可能時間のチェック
    const isInEnableActivateTime = isInStartEnd({ startTime: availableStartAt, endTime: availableEndAt });

    return (
        isBeforeActivateDeadline &&
        isInEnableActivateTime &&
        (orderTicket.distributionType === DISTRIBUTION_TYPE.ENABLE ||
            orderTicket.distributionType === DISTRIBUTION_TYPE.REQUIRED)
    );
};

/**
 * 予約状態キャンセルをチェック。
 */
export const isReservationCanceled = (orderTicket: OrderTicket) => {
    const reservationTickets = orderTicket?.comboItems?.filter((item: any) => item.isReserve);
    const reservationStatusList = reservationTickets.map((reservationTicket: any) => {
        return (
            reservationTicket?.orderUsageTickets?.[0]?.reservationInfo?.reservationStatus ??
            reservationTicket?.orderUsageTickets?.[0]?.reservationStatus
        );
    });

    return reservationStatusList?.includes(RESERVATION_STATUS.CANCELED);
};
